import { http, setHeader, removeHeader } from "@/services/http";
import { formatRelative, isPast } from "date-fns";
import { cloneDeep } from "lodash";
import jwt_decode from "jwt-decode";
import qs from "qs";
import { UserManager } from "oidc-client-ts";
import settings from "@/service/Settings";
import { Log } from "oidc-client-ts";

Log.setLogger(console);
Log.setLevel(Log.DEBUG);

// See https://github.com/IdentityModel/oidc-client-js/wiki for more options
const oidcSettings = {
  authority: settings.OIDC_AUTHORITY,
  client_id: settings.OIDC_CLIENT_ID,
  redirect_uri: new URL("/?oidc_callback", settings.APP_BASE_URL).toString(),
  silent_redirect_uri: new URL(
    "/?oidc_silent_callback",
    settings.APP_BASE_URL
  ).toString(),
  post_logout_redirect_uri: settings.APP_BASE_URL,
  response_type: "code",
  scope: "openid email",
  accessTokenExpiringNotificationTimeInSeconds: 120,
  automaticSilentRenew: true,
};
const oidcUserManager = new UserManager(oidcSettings);

const defaultState = {
  token: null,
  tokenExpiry: null,
  aclFeatures: null,
  roles: [],
  dummyAuthLoggedIn: false,
  impersonateData: {
    auth: {
      token: null,
    },
    companyName: null,
    firstName: null,
    lastName: null,
  },
  administerData: {
    auth: {
      token: null,
    },
    companyName: null,
  },
  userData: {
    companyName: null,
    firstName: null,
    lastName: null,
  },
};

const state = cloneDeep(defaultState);

const getters = {
  stateToken(state) {
    return state.token;
  },
  aclFeatures(state) {
    return state.aclFeatures;
  },
  isLoggedIn(state) {
    return state.token !== null;
  },
  isImpersonating(state) {
    return state.impersonateData.auth.token !== null;
  },
  isAdministering(state) {
    return state.administerData.auth.token !== null;
  },
  activeToken(state, getters) {
    if (getters.isLoggedIn) {
      if (getters.isImpersonating) {
        return state.impersonateData.auth.token;
      } else if (getters.isAdministering) {
        return state.administerData.auth.token;
      }
      return state.token;
    } else {
      return null;
    }
  },
  userData(state, getters) {
    function isAnyPropTruthy(obj) {
      return Object.keys(obj)
        .map((property) => {
          return !!obj[property];
        })
        .some((i) => i);
    }

    if (!isAnyPropTruthy(state.userData)) return null;

    if (getters.isImpersonating) {
      return {
        name: `${state.impersonateData.firstName} ${state.impersonateData.lastName}`,
        company: state.impersonateData.companyName,
      };
    } else {
      return {
        name: `${state.userData.firstName || ""} ${
          state.userData.lastName || ""
        }`,
        company: state.userData.companyName,
      };
    }
  },

  /**
   * Retrieve a function you can use to check for ACL features. You can
   * either pass a single string or an array of strings to this function.
   * The function returns true if every feature is available to the user.
   *
   * @param state Vue internal
   * @returns predicate function
   */
  canI: (state) => (feature) => {
    if (!Array.isArray(state.aclFeatures)) {
      return false;
    }
    if (feature instanceof Array) {
      return feature.reduce((acc, item) => {
        return state.aclFeatures.includes(item) && acc;
      }, true);
    } else {
      return state.aclFeatures.includes(feature);
    }
  },
};

const mutations = {
  token(state, value) {
    state.token = value;
  },
  aclFeatures(state, value) {
    state.aclFeatures = value;
  },
  tokenExpiry(state, value) {
    state.tokenExpiry = value;
  },
  roles(state, value) {
    state.roles = value;
  },
  dummyAuthLoggedIn(state, value) {
    state.dummyAuthLoggedIn = value;
  },
  impersonateData(state, value) {
    state.impersonateData = value;
  },
  administerData(state, value) {
    state.administerData = value;
  },
  userData(state, value) {
    state.userData.firstName = value.firstName;
    state.userData.lastName = value.lastName;
    state.userData.companyName = "";
  },
};

const actions = {
  oidcSigninRedirect: () => oidcUserManager.signinRedirect(),
  oidcSignoutRedirect: () => oidcUserManager.signoutRedirect(),
  oidcSigninSilent: () => oidcUserManager.signinSilent(),
  oidcSigninSilentCallback: () => oidcUserManager.signinSilentCallback(),
  oidcSigninRedirectCallback: () => oidcUserManager.signinRedirectCallback(),
  oidcGetUser: () => oidcUserManager.getUser(),

  oidcEventAccessTokenExpiration: (_, callback) =>
    oidcUserManager.events.addAccessTokenExpiring(callback),
  oidcEventSilentRenewError: (_, callback) =>
    oidcUserManager.events.addSilentRenewError(callback),
  oidcEventUserLoaded: (_, callback) =>
    oidcUserManager.events.addUserLoaded(callback),

  oidcStoreUser(_, user) {
    return oidcUserManager.storeUser(user);
  },

  oidcSetup({ dispatch }) {
    oidcUserManager.events.addSilentRenewError((e) => {
      console.log("Caught renew error", e);
    });
    oidcUserManager.events.addUserLoaded((u) => {
      dispatch("setOidcUser", u);
    });
  },

  dummyAuthLogin({ dispatch, commit }, { username, password }) {
    const querystring = qs.stringify({
      pass: password,
      user: username,
    });

    return new Promise((resolve, reject) => {
      http
        .post(`/api/v2/auth/dummy/login?${querystring}`)
        .then((response) => {
          setHeader("Authorization", "Bearer " + response.data.data.token);
          commit("dummyAuthLoggedIn", true);
          return dispatch("setToken", response.data.data.token);
        })
        .then(resolve)
        .catch((err) => {
          reject(err);
        });
    });
  },

  setOidcUser({ dispatch, commit }, user) {
    commit("token", user.access_token);
    commit("tokenExpiry", user.expires_at);
    setHeader("Authorization", "Bearer " + user.access_token);
    commit("userData", {
      companyName: user.profile.company || null,
      firstName: user.profile.first_name || null,
      lastName: user.profile.last_name || null,
    });
    dispatch("setLogoutTimeout");

    return new Promise((resolve, reject) => {
      http
        .get("/api/v2/users/profile/acl-features")
        .then((response) => {
          commit("aclFeatures", response.data.data.aclFeatures);
        })
        .then(resolve)
        .catch(reject);
    });
  },

  // Removes all user and token data from the frontend, but doesn't issue an OIDC logout
  softLogout({ commit }) {
    return new Promise((resolve) => {
      commit("token", null);
      commit("dummyAuthLoggedIn", false);
      removeHeader("X-Authorization");
      commit("userData", cloneDeep(defaultState.userData));
      commit("impersonateData", cloneDeep(defaultState.impersonateData));
      commit("administerData", cloneDeep(defaultState.administerData));
      commit("brain/maintenanceMode", false, { root: true });
      window.$cookies.remove("n83ksUaBNv44ctev");
      window.$cookies.remove("userData");
      resolve();
    });
  },

  //used after dummyAuthLogin to genuinely become someone else
  takeRole({ commit, state, dispatch }, data) {
    let savedResponse;
    return new Promise((resolve, reject) => {
      http
        .post(`/api/v2/auth/dummy/take-role`, data)
        .then((response) => {
          savedResponse = response;
          commit("dummyAuthLoggedIn", false);
          commit("token", response.data.data.auth.token);
          commit("userData", response.data.data);
          setHeader("Authorization", "Bearer " + state.token);
        })
        .then(() => {
          return dispatch("setLogoutTimeout");
        })
        .then(() => {
          resolve(savedResponse);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  stopImpersonating({ commit, dispatch, getters }) {
    return new Promise((resolve) => {
      commit("impersonateData", cloneDeep(defaultState.impersonateData));
      setHeader("Authorization", "Bearer " + getters.stateToken);
      dispatch("setToken", getters.stateToken);
      resolve();
    });
  },

  stopAdministering({ commit, dispatch, getters }) {
    return new Promise((resolve) => {
      commit("administerData", cloneDeep(defaultState.administerData));
      setHeader("Authorization", "Bearer " + getters.stateToken);
      dispatch("setToken", getters.stateToken);
      resolve();
    });
  },

  setToken({ commit }, value) {
    return new Promise((resolve) => {
      commit("token", value);
      resolve();
    });
  },

  handleImpersonate({ commit }, impersonateJwt) {
    setHeader("Authorization", "Bearer " + impersonateJwt);
    const decodedJwt = jwt_decode(impersonateJwt);
    const impersonateData = {
      auth: {
        token: impersonateJwt,
      },
      companyName:
        decodedJwt.company_names && decodedJwt.company_names.length > 0
          ? decodedJwt.company_names[0]
          : "",
      firstName: decodedJwt.firstName ? decodedJwt.firstName : "",
      lastName: decodedJwt.lastName ? decodedJwt.lastName : "",
    };
    commit("impersonateData", impersonateData);
  },

  handleAdminister({ commit }, administerJwt) {
    setHeader("Authorization", "Bearer " + administerJwt);
    const decodedJwt = jwt_decode(administerJwt);
    const administerData = {
      auth: {
        token: administerJwt,
      },
      companyName:
        decodedJwt.company_names && decodedJwt.company_names.length > 0
          ? decodedJwt.company_names[0]
          : null,
    };
    commit("administerData", administerData);
  },

  setLogoutTimeout({ state, dispatch, getters }) {
    return new Promise((resolve) => {
      if (!getters.isLoggedIn) resolve();

      const expires = state.tokenExpiry * 1000;
      const relativeExpires = formatRelative(expires, new Date());
      console.log("User will log out at: ", relativeExpires);

      if (isPast(expires)) {
        this._vm.$snotify["error"]("Token expired, please log in again.");
        dispatch("softLogout");
      }

      resolve();
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
